import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { HERO } from '@/constants/testIds';

const slides = [
  {
    title: 'Transform Your Business in Next 90 Days',
    subtitle: "Unlock Growth Potential with Oracle's BRM, OAP, and ASAP Solutions!",
    features: ['Billing Revenue Management', 'OBRM Access Portal', 'Automated Service Activation Program'],
    cta: { text: 'Connect', link: '/contact' },
    secondary: { text: 'Know More', link: '/services' },
  },
  {
    title: 'Optimize Billing and Revenue Workflows to Exemplary Standards',
    subtitle: 'Oracle BRM empowers smooth & efficient rating for Billing and Revenue Management across diverse services.',
    features: [],
    cta: { text: 'Learn More', link: '/services' },
    secondary: null,
  },
  {
    title: 'Effortlessly Manage Relations with Our Innovative CRM Solutions',
    subtitle: 'Easily manage customer relationships, sales pipelines, user accounts, pricing plans, promotions & customize your interface with ease.',
    features: [],
    cta: { text: 'Learn More', link: '/services#crm' },
    secondary: null,
  },
  {
    title: 'Streamline Automatic Service Activation for Effortless Setup',
    subtitle: 'Automated service activation program efficiently streamlines customer verification and request validation for seamless compliance.',
    features: [],
    cta: { text: 'Learn More', link: '/services#cloud' },
    secondary: null,
  },
];

const HeroSection = () => {
  const [current, setCurrent] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const slide = slides[current];

  return (
    <section
      data-testid={HERO.section}
      className="relative min-h-screen flex items-center overflow-hidden noise-overlay"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1643228995868-bf698f67d053?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBlbnRlcnByaXNlJTIwc29mdHdhcmUlMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODk3NzE2NTd8MA&ixlib=rb-4.1.0&q=85"
          alt="Hero background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-24 w-full">
        <div className="max-w-3xl" key={current}>
          <div className="carousel-slide">
            <div className="inline-block mb-6 px-4 py-1.5 border border-amber/30 rounded-sm">
              <span className="text-amber text-xs font-mono font-bold uppercase tracking-widest">
                Slide {current + 1} / {slides.length}
              </span>
            </div>

            <h1 data-testid={HERO.title} className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 tracking-tight">
              {slide.title}
            </h1>

            <p data-testid={HERO.subtitle} className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
              {slide.subtitle}
            </p>

            {slide.features.length > 0 && (
              <ul className="space-y-2 mb-8">
                {slide.features.map((feat) => (
                  <li key={feat} className="flex items-center gap-3 text-slate-300">
                    <ArrowRight className="w-4 h-4 text-amber flex-shrink-0" />
                    <span className="text-sm font-medium">{feat}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap gap-4">
              <Link
                data-testid={HERO.connectBtn}
                to={slide.cta.link}
                className="btn-amber rounded-sm text-sm inline-flex items-center gap-2"
              >
                {slide.cta.text} <ArrowRight className="w-4 h-4" />
              </Link>
              {slide.secondary && (
                <Link
                  data-testid={HERO.learnMoreBtn}
                  to={slide.secondary.link}
                  className="btn-outline-light rounded-sm text-sm"
                >
                  {slide.secondary.text}
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Carousel Controls */}
        <div className="absolute bottom-8 right-8 flex items-center gap-3">
          <button
            data-testid={HERO.prevSlide}
            onClick={prevSlide}
            className="w-10 h-10 border border-white/20 rounded-sm flex items-center justify-center text-slate-400 hover:text-amber hover:border-amber transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid={HERO.nextSlide}
            onClick={nextSlide}
            className="w-10 h-10 border border-white/20 rounded-sm flex items-center justify-center text-slate-400 hover:text-amber hover:border-amber transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-6 lg:left-8 flex items-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-1 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-amber' : 'w-4 bg-white/30'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
